import { type ChangeEvent, useEffect, useState } from 'react'

import { trim } from 'commons/utils/string'
import { Alert, Icon, Input, Modal, Select, UploadButton } from 'components'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { getJustificationNDByOrderNumber, saveJustificationNDByOrderNumber } from 'services/draft'
import type { JustificationNDOrder } from 'services/draft/interfaces'
import { getReasonsJustification } from 'services/shared/reasonsJustification'
import type { ReasonsJustification } from 'services/shared/reasonsJustification/types'

import * as S from './styles'

interface OrderOverviewModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  onConfirm: () => void
  onCancel?: () => void
  disabled?: boolean
}

const ModalJustification = ({
  open,
  setOpen,
  disabled = false,
  onConfirm,
  onCancel = () => setOpen(false),
}: OrderOverviewModalProps) => {
  const { orderNumber } = useParams()
  const [loading, setLoading] = useState(false)
  const [reasonsJustificationData, setReasonsJustificationData] = useState<ReasonsJustification[]>(
    [],
  )

  const defaultValues: JustificationNDOrder = {
    file: new File([], ''),
    fileName: '',
    reasonId: '',
    requestDescription: '',
  }

  const methods = useForm({
    defaultValues,
    mode: 'onChange',
  })

  const { getValues, setValue, handleSubmit, reset, watch } = methods

  const fetchReasonsJustification = async () => {
    setLoading(true)
    try {
      setLoading(true)

      const response = await getReasonsJustification()

      setReasonsJustificationData(response.data)
    } catch (error) {
      console.error('Error on fetch reasons justification: ', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchJustificationNDByOrderNumber = async (orderNumber: string) => {
    setLoading(true)

    try {
      const response = await getJustificationNDByOrderNumber(orderNumber)
      const { reasonId, requestDescription, fileName, file } = response.data

      setValue('reasonId', reasonId)
      setValue('requestDescription', requestDescription)
      setValue('fileName', fileName)
      setValue('file', file)
    } catch (error) {
      console.error('Error on fetch reasons justification: ', error)
    } finally {
      setLoading(false)
    }
  }

  // NOTE: Trim necessary, because Select component set ' ' when value is not selected.
  const hasSelectedReasonId = trim(watch('reasonId')) !== ''
  const hasRequestDescription = trim(watch('requestDescription')) !== ''
  const disableFields = disabled || loading || !hasSelectedReasonId

  const handleConfirm = async () => {
    setOpen(false)
    const body = getValues()

    if (hasSelectedReasonId) {
      await saveJustificationNDByOrderNumber({ ...body, orderNumber: orderNumber! })
    }

    onConfirm()
  }

  const handleCancel = () => {
    setOpen(false)
    onCancel()
  }

  useEffect(() => {
    if (open) {
      fetchReasonsJustification()

      if (orderNumber) {
        fetchJustificationNDByOrderNumber(orderNumber)
      }
    } else {
      reset(defaultValues)
    }
  }, [open, orderNumber, reset])

  return (
    <Modal
      buttonAction={handleSubmit(handleConfirm)}
      buttonDisabled={disabled || loading || !hasRequestDescription}
      buttonText="Prosseguir"
      closeAction={handleCancel}
      modalWidth={480}
      open={open}
      returnAction={handleCancel}
      returnIcon={<Icon icon="undo" size="15" />}
      returnText="Voltar"
      setOpen={setOpen}
      showClose={false}
      showReturn
      title="Justificativa - Negociação Diferenciada"
      titleSize={22}
    >
      <S.ContentContainer>
        <Alert
          kind={'warning'}
          message={
            <>
              Pedido sujeito a negociação diferenciada por <strong>desconto</strong> e/ou{' '}
              <strong>prazo</strong> diferente da Política Comercial do cliente.
            </>
          }
          messageOrientation="right"
          title=""
        />
        <Alert
          kind={'info'}
          message={
            <>
              Esta <strong>Justificativa</strong> será aplicada em todas as{' '}
              <strong>Ordens de Venda</strong> geradas por este <strong>pedido.</strong>
            </>
          }
          messageOrientation="right"
          title=""
        />
        <S.FormContainer>
          <FormProvider {...methods}>
            <S.FormFieldContainer>
              <Controller
                name="reasonId"
                render={({ field: { name, onChange, value } }) => (
                  <Select
                    disabled={loading}
                    label="Motivo"
                    menuText="Selecione o motivo"
                    name={name}
                    onChange={onChange}
                    options={reasonsJustificationData.map((reason) => ({
                      title: reason.description,
                      value: reason.id,
                    }))}
                    required
                    value={value}
                  />
                )}
              />
            </S.FormFieldContainer>
            <S.FormFieldContainer>
              <Controller
                name="requestDescription"
                render={({ field: { name, onChange, value } }) => (
                  <Input
                    disabled={disableFields}
                    height={90}
                    label="Descrição"
                    maxLenghtPosition="top"
                    maxLength={100}
                    name={name}
                    onChange={onChange}
                    placeholder="Digite aqui mais informações sobre a solicitação"
                    required
                    rows={3}
                    showMaxLength={true}
                    value={value}
                  />
                )}
              />
            </S.FormFieldContainer>
            <S.FormFieldContainer>
              <Controller
                name="file"
                render={({ field: { onChange, value } }) => (
                  <UploadButton
                    disabled={disableFields}
                    fileName={getValues('fileName')}
                    id="fileInput"
                    key={`fileInput`}
                    name="upload"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      if (e.target.files?.length) {
                        onChange(e.target.files[0])
                        setValue(`fileName`, e.target.files[0].name, {
                          shouldDirty: true,
                        })
                      }
                    }}
                    onClear={() => {
                      setValue('file', new File([], ''))
                      setValue('fileName', '')
                    }}
                    placeholder="Clique aqui para selecionar arquivo"
                    value={value as File}
                  />
                )}
              />
            </S.FormFieldContainer>
          </FormProvider>
        </S.FormContainer>
      </S.ContentContainer>
    </Modal>
  )
}

export default ModalJustification
